import { Trans } from "react-i18next";
import React, { useState } from "react";
import { Card, Form, Button, Row, Col, Badge } from "react-bootstrap";
import { FaSave } from "react-icons/fa";
import { FaUndo } from "react-icons/fa";
import { RegulatoryPhaseGate } from "@shared/hooks/useFeatureFlags";
import "@shared/styles/advanced-eeg-controls.css";

interface AdvancedEEGControlsProps {
  filters: {
    highPass: number;
    lowPass: number;
    notch: boolean;
  };
  reference: string;
  channelGroups: string[];
  waveformColors: Record<string, string>;
  onFiltersChange: (filters: {
    highPass: number;
    lowPass: number;
    notch: boolean;
  }) => void;
  onReferenceChange: (reference: string) => void;
  onChannelGroupsChange: (groups: string[]) => void;
  onWaveformColorsChange: (colors: Record<string, string>) => void;
}

const AdvancedEEGControls: React.FC<AdvancedEEGControlsProps> = ({
  filters,
  reference,
  channelGroups,
  waveformColors,
  onFiltersChange,
  onReferenceChange,
  onChannelGroupsChange,
  onWaveformColorsChange,
}) => {
  const [localFilters, setLocalFilters] = useState(filters);
  const [localReference, setLocalReference] = useState(reference);

  const referenceOptions = [
    { value: "common-average", label: "Common Average Reference" },
    { value: "linked-ears", label: "Linked Ears" },
    { value: "ipsilateral-ears", label: "Ipsilateral Ears" },
    { value: "cz", label: "Cz Reference" },
    { value: "original", label: "Original Reference" },
  ];

  const channelGroupOptions = [
    { value: "frontal", label: "Frontal (Fp1, Fp2, F3, F4, F7, F8, Fz)" },
    { value: "central", label: "Central (C3, C4, Cz)" },
    { value: "temporal", label: "Temporal (T3, T4, T5, T6)" },
    { value: "parietal", label: "Parietal (P3, P4, Pz)" },
    { value: "occipital", label: "Occipital (O1, O2)" },
    { value: "all", label: "All Channels" },
  ];

  const defaultWaveformColors = [
    { channel: "Fp1-F3", color: "#1f77b4" },
    { channel: "F3-C3", color: "#ff7f0e" },
    { channel: "C3-P3", color: "#2ca02c" },
    { channel: "P3-O1", color: "#d62728" },
    { channel: "Fp2-F4", color: "#9467bd" },
    { channel: "F4-C4", color: "#8c564b" },
    { channel: "C4-P4", color: "#e377c2" },
    { channel: "P4-O2", color: "#7f7f7f" },
  ];

  const handleFilterChange = (key: string, value: number | boolean) => {
    const updated = { ...localFilters, [key]: value };
    setLocalFilters(updated);
  };

  const applyFilters = () => {
    onFiltersChange(localFilters);
  };

  const resetFilters = () => {
    const defaults = { highPass: 0.5, lowPass: 70, notch: true };
    setLocalFilters(defaults);
    onFiltersChange(defaults);
  };

  const handleReferenceChange = (value: string) => {
    setLocalReference(value);
    onReferenceChange(value);
  };

  const handleChannelGroupToggle = (group: string) => {
    const updated = channelGroups.includes(group)
      ? channelGroups.filter((g) => g !== group)
      : [...channelGroups, group];
    onChannelGroupsChange(updated);
  };

  return (
    <Card className="advanced-eeg-controls shadow-sm">
      <Card.Body>
        <Card.Title className="d-flex justify-content-between align-items-center mb-3">
          <span><Trans ns="features" i18nKey="ui.copy.a0904f339b21" /></span>
          <div>
            <Button
              size="sm"
              variant="outline-secondary"
              onClick={resetFilters}
              className="me-2"
              aria-label="Reset to default settings"
            >
              <FaUndo className="me-1" /> <Trans ns="features" i18nKey="ui.copy.44c57abd888a" /></Button>
            <Button
              size="sm"
              variant="primary"
              onClick={applyFilters}
              aria-label="Apply filter settings"
            >
              <FaSave className="me-1" /> <Trans ns="features" i18nKey="ui.copy.cfea419c3b4e" /></Button>
          </div>
        </Card.Title>

        <Row>
          {/* Filter Settings */}
          <Col md={6} className="mb-3">
            <h6 className="control-section-title"><Trans ns="features" i18nKey="ui.copy.cddd7eea8771" /></h6>
            <Form.Group className="mb-3">
              <Form.Label>
                <Trans ns="features" i18nKey="ui.copy.f87d8340c2d9" /><strong>{localFilters.highPass} <Trans ns="features" i18nKey="ui.copy.97aaf5bb4065" /></strong>
              </Form.Label>
              <Form.Range
                min={0}
                max={5}
                step={0.1}
                value={localFilters.highPass}
                onChange={(e) =>
                  handleFilterChange("highPass", parseFloat(e.target.value))
                }
                aria-label="High-pass filter frequency"
              />
              <Form.Text className="text-muted">
                <Trans ns="features" i18nKey="ui.copy.e7692e6c568b" /></Form.Text>
            </Form.Group>

            <Form.Group className="mb-3">
              <Form.Label>
                <Trans ns="features" i18nKey="ui.copy.df2772e337a8" /><strong>{localFilters.lowPass} <Trans ns="features" i18nKey="ui.copy.97aaf5bb4065" /></strong>
              </Form.Label>
              <Form.Range
                min={30}
                max={200}
                step={5}
                value={localFilters.lowPass}
                onChange={(e) =>
                  handleFilterChange("lowPass", parseFloat(e.target.value))
                }
                aria-label="Low-pass filter frequency"
              />
              <Form.Text className="text-muted">
                <Trans ns="features" i18nKey="ui.copy.86886c7524e3" /></Form.Text>
            </Form.Group>

            <Form.Group>
              <Form.Check
                type="checkbox"
                id="notch-filter"
                label="60 Hz Notch Filter (Power Line Noise)"
                checked={localFilters.notch}
                onChange={(e) => handleFilterChange("notch", e.target.checked)}
              />
            </Form.Group>
          </Col>

          {/* Reference Configuration */}
          <Col md={6} className="mb-3">
            <h6 className="control-section-title"><Trans ns="features" i18nKey="ui.copy.af6bce7e95c4" /></h6>
            <Form.Group className="mb-3">
              <Form.Label><Trans ns="features" i18nKey="ui.copy.95277be67f17" /></Form.Label>
              <Form.Select
                value={localReference}
                onChange={(e) => handleReferenceChange(e.target.value)}
                aria-label="Select reference type"
              >
                {referenceOptions.map((opt) => (
                  <option key={opt.value} value={opt.value}>
                    {opt.label}
                  </option>
                ))}
              </Form.Select>
              <Form.Text className="text-muted">
                <Trans ns="features" i18nKey="ui.copy.19889c907118" />{" "}
                {
                  referenceOptions.find((r) => r.value === localReference)
                    ?.label
                }
              </Form.Text>
            </Form.Group>

            <h6 className="control-section-title mt-4"><Trans ns="features" i18nKey="ui.copy.6f2627eec7df" /></h6>
            <div className="channel-groups-container">
              {channelGroupOptions.map((group) => (
                <Form.Check
                  key={group.value}
                  type="checkbox"
                  id={`group-${group.value}`}
                  label={group.label}
                  checked={channelGroups.includes(group.value)}
                  onChange={() => handleChannelGroupToggle(group.value)}
                  className="mb-2"
                />
              ))}
            </div>
          </Col>
        </Row>

        {/* Waveform Color Customization */}
        <Row className="mt-3">
          <Col xs={12}>
            <h6 className="control-section-title"><Trans ns="features" i18nKey="ui.copy.c4348a619616" /></h6>
            <div className="waveform-colors-grid">
              {defaultWaveformColors.map(({ channel, color }) => (
                <div key={channel} className="color-picker-item">
                  <Form.Label className="color-label">{channel}</Form.Label>
                  <div className="color-input-wrapper">
                    <Form.Control
                      type="color"
                      value={waveformColors[channel] || color}
                      onChange={(e) =>
                        onWaveformColorsChange({
                          ...waveformColors,
                          [channel]: e.target.value,
                        })
                      }
                      aria-label={`Color for ${channel}`}
                      className="color-picker"
                    />
                    <Badge bg="secondary" className="color-value">
                      {waveformColors[channel] || color}
                    </Badge>
                  </div>
                </div>
              ))}
            </div>
          </Col>
        </Row>

        {/* Quick Presets */}
        <Row className="mt-3">
          <Col xs={12}>
            <h6 className="control-section-title"><Trans ns="features" i18nKey="ui.copy.ad3be8dfe56a" /></h6>
            <div className="preset-buttons">
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setLocalFilters({ highPass: 0.5, lowPass: 70, notch: true });
                  onFiltersChange({ highPass: 0.5, lowPass: 70, notch: true });
                }}
              >
                <Trans ns="features" i18nKey="ui.copy.bd371db7c911" /></Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setLocalFilters({ highPass: 1, lowPass: 35, notch: true });
                  onFiltersChange({ highPass: 1, lowPass: 35, notch: true });
                }}
              >
                <Trans ns="features" i18nKey="ui.copy.b4eb7c2017de" /></Button>
              <Button
                size="sm"
                variant="outline-primary"
                onClick={() => {
                  setLocalFilters({
                    highPass: 0.1,
                    lowPass: 100,
                    notch: false,
                  });
                  onFiltersChange({
                    highPass: 0.1,
                    lowPass: 100,
                    notch: false,
                  });
                }}
              >
                <Trans ns="features" i18nKey="ui.copy.03d8b8ad6058" /></Button>
              <Button
                size="sm"
                variant="outline-secondary"
                onClick={() => {
                  setLocalFilters({ highPass: 0, lowPass: 200, notch: false });
                  onFiltersChange({ highPass: 0, lowPass: 200, notch: false });
                }}
              >
                <Trans ns="features" i18nKey="ui.copy.1d525223810a" /></Button>
            </div>
          </Col>
        </Row>
      </Card.Body>
    </Card>
  );
};

/**
 * Phase-gated wrapper — AdvancedEEGControls requires Phase 2 (Signal Processing)
 * regulatory clearance because it provides signal filtering controls (high-pass,
 * low-pass, notch filters) and re-referencing, which constitute data alteration.
 *
 * Named export `AdvancedEEGControls` is available for direct access in tests.
 */
const GatedAdvancedEEGControls: React.FC<AdvancedEEGControlsProps> = (
  props,
) => (
  <RegulatoryPhaseGate phase={2}>
    <AdvancedEEGControls {...props} />
  </RegulatoryPhaseGate>
);

export { AdvancedEEGControls };
export default GatedAdvancedEEGControls;
